import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, put } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Skeleton } from '@/components/atoms/Skeleton';

interface Setting {
  key: string;
  value: string | null;
  description: string | null;
  sort_order: number;
}

/**
 * 어드민 사이트 설정 — 단순 key/value 메타 (footer 사업자 정보 등).
 * 행별 수정 → 저장 버튼.
 */
export function SiteSettingsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'site-settings'],
    queryFn: () => get<{ settings: Setting[] }>('/admin/site-settings'),
  });

  const [draft, setDraft] = useState<Record<string, string>>({});

  // 데이터 로드 시 초기 draft 설정
  useEffect(() => {
    if (!data) return;
    const next: Record<string, string> = {};
    data.settings.forEach((s) => {
      next[s.key] = s.value ?? '';
    });
    setDraft(next);
  }, [data]);

  const updateMut = useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      put(`/admin/site-settings/${encodeURIComponent(key)}`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'site-settings'] }),
  });

  return (
    <div className="w-full">
      <h1 className="mb-4 text-2xl font-extrabold">사이트 설정</h1>
      <p className="mb-6 text-sm text-[var(--color-text-sub)]">
        푸터 사업자 정보 등 사이트 전체에 노출되는 값. 변경 즉시 사용자 페이지에 반영됩니다.
      </p>

      {isLoading ? (
        <Skeleton className="h-80" />
      ) : (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          {data?.settings.map((s) => (
            <div
              key={s.key}
              className="grid grid-cols-1 gap-2 border-t border-[var(--color-border)] p-4 first:border-t-0 sm:grid-cols-[180px_1fr_auto] sm:items-center"
            >
              <div>
                <div className="text-sm font-semibold">{s.description ?? s.key}</div>
                <code className="text-[10px] text-[var(--color-text-sub)]">{s.key}</code>
              </div>
              <Input
                value={draft[s.key] ?? ''}
                onChange={(e) => setDraft({ ...draft, [s.key]: e.target.value })}
              />
              <Button
                size="sm"
                disabled={(draft[s.key] ?? '') === (s.value ?? '')}
                loading={updateMut.isPending && updateMut.variables?.key === s.key}
                onClick={() => updateMut.mutate({ key: s.key, value: draft[s.key] ?? '' })}
              >
                저장
              </Button>
            </div>
          ))}
          {data?.settings.length === 0 && (
            <div className="p-8 text-center text-sm text-[var(--color-text-sub)]">
              등록된 설정이 없어요.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
